import * as React from 'react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Box,
  IconButton,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import valmetLogo from '../assets/valmet_logo@3x.png';
import { AuthContext } from '../store/authContext';
import Navigation from '../navigation/Navigation';
import HideOnScroll from './HideOnScroll';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 4px 4px #0000003D',
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    display: 'flex',
    paddingRight: 0,
  },
  appBarShift: {
    width: '100%',
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    flexGrow: 1,
  },
  logo: {
    height: '3rem',
  },
  rightContainer: {
    marginLeft: 'auto',
    display: 'flex',
    height: '100%',
  },
  paperRounded: {
    borderRadius: 2,
  },
  menuListItem: {
    paddingRight: theme.spacing(8),
  },
  moreButton: {
    alignSelf: 'center',
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  menuIcon: {
    minWidth: 36,
  },
}));

const AppBar: React.FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const { t } = useTranslation();
  const history = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const { logout, isLoggedIn } = useContext(AuthContext);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    logout();
    history('/');
    handleClose();
  };

  return (
    <HideOnScroll>
      <MuiAppBar
        position="fixed"
        sx={(theme) => ({
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0px 4px 4px #0000003D',
          zIndex: theme.zIndex.drawer + 1,
        })}
      >
        <Toolbar
          sx={{
            display: 'flex',
            paddingRight: 0,
          }}
        >
          <Box
            component="img"
            src={valmetLogo}
            alt="Valmet logo"
            sx={{ height: '3rem' }}
          />
          <Box
            sx={{
              marginLeft: 'auto',
              display: 'flex',
              height: '100%',
            }}
          >
            {!isSmall && isLoggedIn && <Navigation />}
            <IconButton
              color="secondary"
              onClick={handleClick}
              sx={(theme) => ({
                alignSelf: 'center',
                marginRight: theme.spacing(1),
                marginLeft: theme.spacing(2),
              })}
              size="large"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={open}
              onClose={handleClose}
              slotProps={{ backdrop: { invisible: false } }}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: 2,
                },
              }}
            >
              {isLoggedIn && (
                <MenuItem
                  onClick={handleLogout}
                  sx={(theme) => ({
                    paddingRight: theme.spacing(8),
                  })}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ExitToAppIcon />
                  </ListItemIcon>

                  <ListItemText primary={t('auth:logout')} />
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
        {isSmall && isLoggedIn && <Navigation />}
      </MuiAppBar>
    </HideOnScroll>
  );
};

export default AppBar;
